import { Component, OnDestroy, OnInit } from '@angular/core'
import { FormControl, FormGroup, Validators } from '@angular/forms'
import { ActivatedRoute, Router } from '@angular/router'
import { ToastrService } from 'ngx-toastr'
import { Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import { INTERNATIONAL_PHONE_CODE, PHONE_LENGTH } from 'src/app/core/config'
import { APP_ROUTES } from 'src/app/core/config/routes'
import { USER_TYPES } from 'src/app/core/enums'
import { LayoutService } from 'src/app/shared/services/layout.service'
import { SharedService } from 'src/app/shared/services/shared.service'
import { AuthService } from '../../services/auth.service'

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit, OnDestroy {
  loginForm: FormGroup
  phoneCode = INTERNATIONAL_PHONE_CODE
  phoneLength: number = PHONE_LENGTH
  APP_ROUTES = APP_ROUTES
  showPassword = false
  isLoading = false

  componentDestroyed$ = new Subject<boolean>()
  isDemoMode: boolean

  userTypes = USER_TYPES
  type: any

  // Account selection state
  showAccountSelection = false
  availableAccounts: any[] = []
  showPhoneInput = true

  // Arabic to English numeral mapping
  private arabicToEnglishMap: { [key: string]: string } = {
    '٠': '0',
    '١': '1',
    '٢': '2',
    '٣': '3',
    '٤': '4',
    '٥': '5',
    '٦': '6',
    '٧': '7',
    '٨': '8',
    '٩': '9'
  }

  constructor(
    private authService: AuthService,
    private sharedService: SharedService,
    private router: Router,
    private toastr: ToastrService,
    private layoutService: LayoutService,
    private activatedRoute: ActivatedRoute
  ) {
    window.scroll({
      top: 0,
      left: 0,
      behavior: 'smooth'
    })
  }

  ngOnInit(): void {
    this.initializeForm()

    this.sharedService
      .getDemoStatus()
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(status => {
        this.isDemoMode = status
      })
  }

  initializeForm() {
    this.loginForm = new FormGroup({
      phoneNumber: new FormControl('', {
        validators: [
          Validators.required,
          Validators.maxLength(this.phoneLength),
          Validators.minLength(this.phoneLength),
          Validators.pattern(/^[\d٠١٢٣٤٥٦٧٨٩]+$/) // Allow both Arabic and English digits
        ]
      }),
      pwd: new FormControl('', []),
      pairingCode: new FormControl('', []) // Added pairingCode control
    })
  }

  /**
   * Convert Arabic numerals to English numerals in real-time
   */
  convertArabicNumbers(event: any): void {
    const input = event.target as HTMLInputElement;
    let value = input.value;
    
    // Convert Arabic numerals to English
    const convertedValue = value.split('').map(char => 
      this.arabicToEnglishMap[char] || char
    ).join('');
    
    // Update the form control value with English numerals
    if (value !== convertedValue) {
      this.loginForm.get('phoneNumber')?.setValue(convertedValue, { emitEvent: false });
      
      // Set the input value directly to maintain cursor position
      setTimeout(() => {
        input.value = convertedValue;
      });
    }
  }

  /**
   * Normalize phone number by converting any Arabic numerals to English
   */
  private normalizePhoneNumber(phoneNumber: string): string {
    return phoneNumber.split('').map(char => 
      this.arabicToEnglishMap[char] || char
    ).join('');
  }

  resetForm() {
    this.loginForm.reset();
    this.showPassword = false;
    this.showAccountSelection = false;
    this.availableAccounts = [];
    this.showPhoneInput = true;
    this.loginForm.get('pwd')?.clearValidators();
    this.loginForm.get('pwd')?.updateValueAndValidity();
    this.loginForm.get('pairingCode')?.clearValidators();
    this.loginForm.get('pairingCode')?.updateValueAndValidity();
    this.isLoading = false;
  }

  onSubmit() {
    if (this.loginForm.invalid) {
      return
    }

    this.isLoading = true

    // Normalize the phone number (convert Arabic to English numerals)
    const normalizedPhoneNumber = this.normalizePhoneNumber(this.loginForm.value.phoneNumber);
    const phoneNumber = this.phoneCode + normalizedPhoneNumber;

    // Lookup all accounts for this phone number
    this.authService.lookupAccounts(phoneNumber).subscribe(
      (response: any) => {
        const accounts = response?.data?.accounts || response?.accounts || [];
        const count = response?.data?.count || response?.count || 0;

        if (count === 0) {
          this.isLoading = false;
          this.toastr.error('رقم الهاتف غير مسجل لدينا');
          return;
        }

        if (count === 1) {
          // Single account - proceed automatically
          const account = accounts[0];
          this.type = account.type;
          this.authService.type = account.type;
          this.handleSingleAccount(phoneNumber, account.type);
        } else {
          // Multiple accounts - show selection
          this.availableAccounts = accounts;
          this.showAccountSelection = true;
          this.showPhoneInput = false;
          this.isLoading = false;
        }
      },
      error => {
        this.isLoading = false;
        this.toastr.error('رقم الهاتف غير مسجل لدينا');
      }
    );
  }

  handleSingleAccount(phoneNumber: string, userType: string) {
    if (
      [
        this.userTypes.TRANSPORTER,
        this.userTypes.TRANSPORTER_AGENT,
        this.userTypes.TRANSPORTER_COMPANY,
        this.userTypes.CONTROLLER,
        this.userTypes.MARKETER
      ].includes(userType as any)
    ) {
      // Show password field for these types
      this.showPassword = true;

      // Add required validator if showing password field
      this.loginForm.get('pwd')?.setValidators([Validators.required]);
      this.loginForm.get('pwd')?.updateValueAndValidity();

      // Special case for marketer
      if (userType === this.userTypes.MARKETER) {
        this.loginForm
          .get('pairingCode')
          ?.setValidators([Validators.required]);
        this.loginForm.get('pairingCode')?.updateValueAndValidity();
      }

      this.isLoading = false;
    } else {
      // For other types, proceed with OTP flow
      this.proceedWithOtp(phoneNumber, userType);
    }
  }

  selectAccount(accountType: string) {
    this.type = accountType;
    this.authService.type = accountType;
    const normalizedPhoneNumber = this.normalizePhoneNumber(this.loginForm.value.phoneNumber);
    const phoneNumber = this.phoneCode + normalizedPhoneNumber;
    this.showAccountSelection = false;
    this.handleSingleAccount(phoneNumber, accountType);
  }

  getAccountTypeLabel(type: string): string {
    const labels: { [key: string]: string } = {
      [this.userTypes.BUYER]: 'مشتري',
      [this.userTypes.SELLER]: 'بائع',
      [this.userTypes.BUYER_SELLER]: 'مشتري وبائع',
      [this.userTypes.TRANSPORTER_AGENT]: 'ناقل',
      [this.userTypes.TRANSPORTER_COMPANY]: 'شركة نقل',
      [this.userTypes.MARKETER]: 'مسوق',
      [this.userTypes.CONTROLLER]: 'مراقب'
    };
    return labels[type] || type;
  }

  proceedWithLogin() {
    if (this.loginForm.invalid) {
      return
    }

    this.isLoading = true
    
    // Normalize the phone number (convert Arabic to English numerals)
    const normalizedPhoneNumber = this.normalizePhoneNumber(this.loginForm.value.phoneNumber);
    
    const requestData = {
      phoneNumber: this.phoneCode + normalizedPhoneNumber,
      pwd: this.loginForm.value.pwd,
      pairingCode: this.loginForm.value.pairingCode
    }

    this.authService.signin(this.type, requestData).subscribe(
      (response: any) => {
        if (!this.sharedService?.isMock) {
          localStorage.setItem('user', JSON.stringify(response.data.user))
          localStorage.setItem('token', response.data.token)
        }
        this.toastr.success('! تم تسجيل الدخول بنجاح')
        this.layoutService.initializeSidebarListenners()
        this.router.navigate(['/'])
        this.isLoading = false
      },
      error => {
        this.isLoading = false
        this.toastr.error('بيانات الدخول غير صحيحة')
      }
    )
  }

  proceedWithOtp(phoneNumber: string, userType: string) {
    this.authService.sendOtp(phoneNumber, userType).subscribe(
      response => {
        if (response) {
          this.toastr.success('أرسلنا رمز التفعيل إلى الهاتف')
          this.router.navigate([APP_ROUTES.AUTH.OTP], {
            state: { phoneNumber, userType }
          })
        }
        this.isLoading = false
      },
      error => {
        this.isLoading = false
        this.toastr.error('حدث خطأ أثناء إرسال رمز التحقق')
      }
    )
  }

  ngOnDestroy(): void {
    this.componentDestroyed$.complete()
  }
}